import { useContext, useEffect, useLayoutEffect, useState, useRef } from "react";
import Draggable, { DraggableData, DraggableEvent } from "react-draggable";
import { ActionType, hexHeight, hexWidth } from "../State/State";
import { getGlobalDispatch, getGlobalState } from "../State/Context";
import { fetchVideosInBBox } from "../api/hexagen";
import { Hexagon } from "./Hexagon";
import { resetLoadOrder } from "./hexLoadOrder";
import { notifyPanStopped } from "./hexTileBatch";
import { isMobile, imgTileFromCoords } from "../utils/settings";
import { videoDebugLog } from "../utils/videoDebugLog";
import "./World.scss";
import { AppContext } from "../State/Context";

/** Ring 0 = index 0 (1 hex), ring 1 = indices 1..6 (6), ring 2 = 7..18 (12), etc. */
function getRingIndex(orderIndex: number): number {
  if (orderIndex === 0) return 0;
  let start = 1;
  let r = 1;
  while (start + 6 * r <= orderIndex) {
    start += 6 * r;
    r++;
  }
  return r;
}

export const World = () => {
  const { state, dispatch } = useContext(AppContext);
  const [hexEllements, setHexElements] = useState<JSX.Element[]>([]);

  const nodeRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const bboxRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  const videosFetchTimerRef = useRef<number | null>(null);
  const lastVideosFetchAtRef = useRef<number>(0);
  const isDraggingRef = useRef<boolean>(false);
  const worldXRef = useRef<number>(0);
  const worldYRef = useRef<number>(0);
  const worldScaleRef = useRef<number>(1);
  const wheelVelRef = useRef<{ vx: number; vy: number }>({ vx: 0, vy: 0 });
  const wheelEndTimerRef = useRef<number | null>(null);
  const inertiaVelRef = useRef<{ vx: number; vy: number } | null>(null);
  const inertiaFromTouchRef = useRef<boolean>(false);
  const lastDispatchedRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const worldRafRef = useRef<number | null>(null);
  const dragRafRef = useRef<number | null>(null);
  const pendingDragRef = useRef<{ x: number; y: number } | null>(null);
  const lastGridBoundsRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  const hexGridBufferRef = useRef<number>(0);
  const gridIdRef = useRef<number>(0);
  /** Last N drag (t,x,y) samples for touch velocity on mobile */
  const touchDragSamplesRef = useRef<{ t: number; x: number; y: number }[]>([]);
  const TOUCH_SAMPLES_MAX = 8;
  const lastDispatchAtRef = useRef<number>(0);
  const DRAG_DISPATCH_THROTTLE_MS = 120;

  const isDragging = state.isWorldDragging;

  const updateHexGridIfBoundsChanged = (worldX: number, worldY: number, scale: number) => {
    const buf = state.hexGridBuffer;
    const bounds = getVisibleHexBounds(worldX, worldY, scale, buf);
    const last = lastGridBoundsRef.current;
    if (
      last &&
      last.minX === bounds.minX &&
      last.minY === bounds.minY &&
      last.maxX === bounds.maxX &&
      last.maxY === bounds.maxY
    ) {
      return;
    }
    lastGridBoundsRef.current = bounds;
    gridIdRef.current += 1;
    const videoCoords = state.hasVideo || [];
    const { elements, ringSizes } = generateHexesWithRingSizes(worldX, worldY, scale, buf, gridIdRef.current, videoCoords);
    resetLoadOrder(gridIdRef.current, ringSizes);
    videoDebugLog("grid update", {
      bounds: { minX: bounds.minX, minY: bounds.minY, maxX: bounds.maxX, maxY: bounds.maxY },
      elementsCount: elements.length,
      videoCoordsCount: videoCoords.length,
    });
    setHexElements(elements);
  };

  useEffect(() => {
    isDraggingRef.current = state.isWorldDragging;
    worldXRef.current = state.worldX;
    worldYRef.current = state.worldY;
    worldScaleRef.current = state.worldScale;
    hexGridBufferRef.current = state.hexGridBuffer;
    lastDispatchedRef.current = { x: state.worldX, y: state.worldY };
  }, [state.isWorldDragging, state.worldX, state.worldY, state.worldScale, state.hexGridBuffer]);

  useEffect(() => {
    lastGridBoundsRef.current = null;
  }, [(state.hasVideo || []).length]);

  useEffect(() => {
    const handleResize = () => {
      lastGridBoundsRef.current = null;
      updateHexGridIfBoundsChanged(state.worldX, state.worldY, state.worldScale);
      bboxRef.current = getVisibleHexBounds(state.worldX, state.worldY, state.worldScale, state.hexGridBuffer);
    };
    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [state.worldX, state.worldY, state.worldScale, state.hexGridBuffer]);

  useEffect(() => {
    scheduleDebouncedVideosFetch();
  }, []);

  useEffect(() => {
    scheduleDebouncedVideosFetch();
  }, [state.worldScale, state.worldX, state.worldY]);

  useEffect(() => {
    const centerCoords = getCenterHexCoords(state.worldX, state.worldY, state.worldScale, state.hexGridBuffer);
    const preloadId = "hexagen-lcp-preload";
    document.querySelectorAll(`link[data-${preloadId}]`).forEach((el) => el.remove());
    centerCoords.slice(0, 5).forEach(({ x, y }) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = imgTileFromCoords(x, y, state.highQuality);
      link.setAttribute("data-" + preloadId, "1");
      document.head.appendChild(link);
    });
    return () => {
      document.querySelectorAll(`link[data-${preloadId}]`).forEach((el) => el.remove());
    };
  }, [state.worldX, state.worldY, state.worldScale, state.highQuality, state.hexGridBuffer]);

  const scheduleDebouncedVideosFetch = () => {
    if (videosFetchTimerRef.current) {
      clearTimeout(videosFetchTimerRef.current);
      videosFetchTimerRef.current = null;
    }
    videosFetchTimerRef.current = window.setTimeout(async () => {
      const now = Date.now();
      const since = now - lastVideosFetchAtRef.current;
      if (since < 2000) {
        videosFetchTimerRef.current = window.setTimeout(() => {
          scheduleDebouncedVideosFetch();
        }, 2000 - since);
        return;
      }

      const scale = worldScaleRef.current;
      const buf = hexGridBufferRef.current;
      const bounds = getVisibleHexBounds(worldXRef.current, worldYRef.current, scale, buf);
      const newList = await fetchVideosInBBox(bounds.minX, bounds.minY, bounds.maxX, bounds.maxY).catch(() => null);
      if (newList === null) return;
      lastVideosFetchAtRef.current = Date.now();
      const dispatch = getGlobalDispatch();
      const prevList = getGlobalState().hasVideo || [];
      const prevSet = new Set(prevList.map((h) => `${h.x},${h.y}`));
      const merged: { x: number; y: number }[] = [...prevList];
      for (const h of newList) {
        if (!prevSet.has(`${h.x},${h.y}`)) {
          prevSet.add(`${h.x},${h.y}`);
          merged.push(h);
        }
      }
      const currentAfterAwait = getGlobalState().hasVideo || [];
      if (currentAfterAwait.length > merged.length) {
        const curSet = new Set(merged.map((h) => `${h.x},${h.y}`));
        for (const h of currentAfterAwait) {
          if (!curSet.has(`${h.x},${h.y}`)) {
            curSet.add(`${h.x},${h.y}`);
            merged.push(h);
          }
        }
      }
      videoDebugLog("SET_HAS_VIDEO_LIST", {
        prevLen: prevList.length,
        newFromApi: newList.length,
        mergedLen: merged.length,
        currentAfterAwaitLen: currentAfterAwait.length,
      });
      dispatch({ type: ActionType.SET_HAS_VIDEO_LIST, list: merged });
    }, 200);
  };

  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    const x = Math.round(data.x * state.worldScale);
    const y = Math.round(data.y * state.worldScale);
    updateHexGridIfBoundsChanged(x, y, state.worldScale);

    if (dragRafRef.current !== null) {
      cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }
    pendingDragRef.current = null;

    if (isMobile()) {
      const samples = touchDragSamplesRef.current;
      if (samples.length >= 2) {
        const first = samples[0];
        const last = samples[samples.length - 1];
        const dt = (last.t - first.t) / 1000;
        if (dt > 0.01) {
          const TOUCH_VEL_FACTOR = 0.04;
          const TOUCH_VEL_MAX = 55;
          let vx = ((last.x - first.x) / dt) * TOUCH_VEL_FACTOR;
          let vy = ((last.y - first.y) / dt) * TOUCH_VEL_FACTOR;
          const mag = Math.hypot(vx, vy);
          if (mag > TOUCH_VEL_MAX) {
            const s = TOUCH_VEL_MAX / mag;
            vx *= s;
            vy *= s;
          }
          inertiaFromTouchRef.current = true;
          inertiaVelRef.current = { vx, vy };
        }
      }
      touchDragSamplesRef.current = [];
    } else {
      inertiaFromTouchRef.current = false;
    }

    requestAnimationFrame(() =>
      dispatch({ type: ActionType.STOP_WORLD_DRAGGING, logggable: false })
    );

    const isCoordinateUpdated = state.worldX !== x || state.worldY !== y;
    if (isCoordinateUpdated) {
      dispatch({ type: ActionType.CHANGE_WORLD_COORDINATES, x, y });
    }

    scheduleDebouncedVideosFetch();
    notifyPanStopped();
  };

  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    if (isMobile()) {
      const arr = touchDragSamplesRef.current;
      arr.push({ t: Date.now(), x: data.x, y: data.y });
      if (arr.length > TOUCH_SAMPLES_MAX) arr.shift();
    }

    const roundTo = 5;
    const x = Math.round(data.x / roundTo);
    const y = Math.round(data.y / roundTo);
    const stateWorldX = Math.round(state.worldX / roundTo);
    const stateWorldY = Math.round(state.worldY / roundTo);

    if (!isDragging && (stateWorldX !== x || stateWorldY !== y)) {
      dispatch({ type: ActionType.START_WORLD_DRAGGING, logggable: false });
    }

    const scale = worldScaleRef.current;
    const sx = data.x * scale;
    const sy = data.y * scale;
    worldXRef.current = sx;
    worldYRef.current = sy;
    pendingDragRef.current = { x: sx, y: sy };
    if (dragRafRef.current === null) {
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null;
        const p = pendingDragRef.current;
        if (!p) return;
        const now = Date.now();
        const node = nodeRef.current;
        if (node) {
          const tx = p.x / scale;
          const ty = p.y / scale;
          node.style.transform = `translate3d(${tx}px,${ty}px,0)`;
        }
        updateHexGridIfBoundsChanged(p.x, p.y, scale);
        if (now - lastDispatchAtRef.current >= DRAG_DISPATCH_THROTTLE_MS) {
          lastDispatchAtRef.current = now;
          lastDispatchedRef.current = { x: Math.round(p.x), y: Math.round(p.y) };
          dispatch({ type: ActionType.CHANGE_WORLD_COORDINATES, x: p.x, y: p.y });
        }
      });
    }

    if (videosFetchTimerRef.current) {
      clearTimeout(videosFetchTimerRef.current);
      videosFetchTimerRef.current = null;
    }
    scheduleDebouncedVideosFetch();
  };

  useEffect(() => {
    return () => {
      if (videosFetchTimerRef.current) {
        clearTimeout(videosFetchTimerRef.current);
        videosFetchTimerRef.current = null;
      }
    };
  }, []);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    const s = worldScaleRef.current;
    const tx = worldXRef.current / s;
    const ty = worldYRef.current / s;
    node.style.transform = `translate3d(${tx}px,${ty}px,0)`;
  }, [hexEllements]);

  useEffect(() => {
    const tick = () => {
      const scale = worldScaleRef.current;
      const vel = inertiaVelRef.current;
      if (vel) {
        worldXRef.current += vel.vx;
        worldYRef.current += vel.vy;
        const decay = inertiaFromTouchRef.current ? 0.82 : 0.92;
        vel.vx *= decay;
        vel.vy *= decay;
        if (Math.abs(vel.vx) < 0.5 && Math.abs(vel.vy) < 0.5) {
          inertiaVelRef.current = null;
          inertiaFromTouchRef.current = false;
          lastDispatchedRef.current = { x: Math.round(worldXRef.current), y: Math.round(worldYRef.current) };
          dispatch({ type: ActionType.CHANGE_WORLD_COORDINATES, x: lastDispatchedRef.current.x, y: lastDispatchedRef.current.y });
          scheduleDebouncedVideosFetch();
          notifyPanStopped();
        }
      }
      const x = worldXRef.current;
      const y = worldYRef.current;
      if (!isDraggingRef.current && nodeRef.current) {
        const tx = x / scale;
        const ty = y / scale;
        nodeRef.current.style.transform = `translate3d(${tx}px,${ty}px,0)`;
      }
      updateHexGridIfBoundsChanged(x, y, scale);
      worldRafRef.current = requestAnimationFrame(tick);
    };
    worldRafRef.current = requestAnimationFrame(tick);
    return () => {
      if (worldRafRef.current) cancelAnimationFrame(worldRafRef.current);
    };
  }, [dispatch]);

  useEffect(() => {
    if (isMobile()) return;
    const el = wrapperRef.current;
    if (!el) return;

    const startInertia = () => {
      const { vx, vy } = wheelVelRef.current;
      if (Math.abs(vx) < 0.5 && Math.abs(vy) < 0.5) {
        lastDispatchedRef.current = { x: Math.round(worldXRef.current), y: Math.round(worldYRef.current) };
        dispatch({ type: ActionType.CHANGE_WORLD_COORDINATES, x: lastDispatchedRef.current.x, y: lastDispatchedRef.current.y });
        return;
      }
      inertiaVelRef.current = { vx, vy };
    };

    const panDirection = state.invertMapPan ? 1 : -1;
    const wheelPanFactor = 0.55;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (isDraggingRef.current) return;
      const dx = panDirection * e.deltaX * wheelPanFactor;
      const dy = panDirection * e.deltaY * wheelPanFactor;
      worldXRef.current += dx;
      worldYRef.current += dy;
      wheelVelRef.current = { vx: dx * 0.25, vy: dy * 0.25 };

      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
      wheelEndTimerRef.current = window.setTimeout(() => {
        wheelEndTimerRef.current = null;
        startInertia();
      }, 80);
    };

    el.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => {
      el.removeEventListener("wheel", onWheel, true);
      if (wheelEndTimerRef.current) clearTimeout(wheelEndTimerRef.current);
    };
  }, [dispatch, state.invertMapPan]);

  // Block Chrome swipe-back: prevent touchmove default in capture so history gesture never fires
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => e.preventDefault();
    el.addEventListener("touchmove", onTouchMove, { passive: false, capture: true });
    return () => el.removeEventListener("touchmove", onTouchMove, true);
  }, []);

  return (
    <div ref={wrapperRef} className="wrapper absolute top-0 bottom-0 left-0 right-0 z-0 bg-black overflow-hidden">
      <div
        className="absolute z-10 origin-top-left"
        style={{ willChange: "transform", transform: `scale(${state.worldScale})` }}
      >
        <Draggable
          nodeRef={nodeRef}
          onStop={handleDragStop}
          onDrag={handleDrag}
          position={{ x: state.worldX / state.worldScale, y: state.worldY / state.worldScale }}
        >
          <div
            ref={nodeRef}
            className="field absolute z-10 cursor-grab active:cursor-grabbing touch-none"
            style={{ transition: "none", willChange: "transform" }}
          >
            <div className="w-screen h-screen" />
            {hexEllements}
          </div>
        </Draggable>
      </div>
    </div>
  );
};

/** Hex (x,y) center position in world pixels (same as Hexagon layout). */
function hexCenterInWorld(
  x: number,
  y: number,
  scale: number
): { cx: number; cy: number } {
  const w = hexWidth * scale;
  const h = hexHeight * scale;
  const evenGap = y % 2 ? w / 2 : 0;
  const left = x * w + evenGap;
  const top = y * h - hexHeight / 2;
  return { cx: left + w / 2, cy: top + h / 2 };
}

/** Sort by distance from viewport center. Used only for load priority (which hex to load first), not for DOM order. */
function sortHexCoordsByDistance(
  coords: { x: number; y: number }[],
  worldX: number,
  worldY: number,
  worldScale: number
): { x: number; y: number }[] {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const refX = W / 2 - worldX;
  const refY = H / 2 - worldY;

  return [...coords].sort((a, b) => {
    const { cx: ax, cy: ay } = hexCenterInWorld(a.x, a.y, worldScale);
    const { cx: bx, cy: by } = hexCenterInWorld(b.x, b.y, worldScale);
    const da = (ax - refX) ** 2 + (ay - refY) ** 2;
    const db = (bx - refX) ** 2 + (by - refY) ** 2;
    return da - db;
  });
}

/** Canonical (y,x) order so the same set of coords always yields the same list order; on scroll we only add/remove nodes, never reorder. */
function coordsInCanonicalOrder(coords: { x: number; y: number }[]): { x: number; y: number }[] {
  return [...coords].sort((a, b) => (a.y !== b.y ? a.y - b.y : a.x - b.x));
}

/**
 * Visible hex bounds. Draggable position (worldX, worldY) puts content (0,0) at screen (worldX, worldY),
 * so visible content in content coords is [-worldX, winW-worldX] x [-worldY, winH-worldY].
 * Hex at (x,y) has left = x*W + (y%2?W/2:0), top = y*H - hexHeight/2.
 * Base = minimal hex range covering the view (with ±W/2, ±hexHeight/2 for layout). Buffer adds exactly that many extra rows/cols on each side.
 */
function getVisibleHexBounds(
  worldX: number,
  worldY: number,
  worldScale: number,
  hexGridBuffer: number
): { minX: number; minY: number; maxX: number; maxY: number } {
  const winW = window.innerWidth;
  const winH = window.innerHeight;
  const W = hexWidth * worldScale;
  const H = hexHeight * worldScale;
  const buf = hexGridBuffer;
  const contentLeft = -worldX;
  const contentRight = winW - worldX;
  const contentTop = -worldY;
  const contentBottom = winH - worldY;
  const baseMinX = Math.floor((contentLeft - W / 2) / W);
  const baseMaxX = Math.ceil((contentRight + W / 2) / W) - 1;
  const baseMinY = Math.floor((contentTop - hexHeight / 2) / H);
  const baseMaxY = Math.floor((contentBottom + hexHeight / 2) / H);
  const minX = baseMinX - buf;
  const maxX = baseMaxX + buf;
  const minY = baseMinY - buf;
  const maxY = baseMaxY + buf;
  return { minX, minY, maxX, maxY };
}

function getCenterHexCoords(
  worldX: number,
  worldY: number,
  worldScale: number,
  hexGridBuffer: number
): { x: number; y: number }[] {
  const bounds = getVisibleHexBounds(worldX, worldY, worldScale, hexGridBuffer);
  const coords: { x: number; y: number }[] = [];
  for (let x = bounds.minX; x <= bounds.maxX; x++) {
    for (let y = bounds.minY; y <= bounds.maxY; y++) {
      coords.push({ x, y });
    }
  }
  return sortHexCoordsByDistance(coords, worldX, worldY, worldScale).slice(0, 5);
}

const VIDEO_HEX_KEEP_MARGIN = 5;

function generateHexesWithRingSizes(
  worldX: number,
  worldY: number,
  worldScale: number,
  hexGridBuffer: number,
  loadGridId: number,
  videoCoords: { x: number; y: number }[] = []
): { elements: JSX.Element[]; ringSizes: number[] } {
  const bounds = getVisibleHexBounds(worldX, worldY, worldScale, hexGridBuffer);
  const coordSet = new Set<string>();
  const coords: { x: number; y: number }[] = [];
  for (let x = bounds.minX; x <= bounds.maxX; x++) {
    for (let y = bounds.minY; y <= bounds.maxY; y++) {
      const k = `${x},${y}`;
      if (!coordSet.has(k)) {
        coordSet.add(k);
        coords.push({ x, y });
      }
    }
  }
  const minX = bounds.minX - VIDEO_HEX_KEEP_MARGIN;
  const maxX = bounds.maxX + VIDEO_HEX_KEEP_MARGIN;
  const minY = bounds.minY - VIDEO_HEX_KEEP_MARGIN;
  const maxY = bounds.maxY + VIDEO_HEX_KEEP_MARGIN;
  for (const h of videoCoords) {
    if (h.x >= minX && h.x <= maxX && h.y >= minY && h.y <= maxY) {
      const k = `${h.x},${h.y}`;
      if (!coordSet.has(k)) {
        coordSet.add(k);
        coords.push({ x: h.x, y: h.y });
      }
    }
  }
  const byDistance = sortHexCoordsByDistance(coords, worldX, worldY, worldScale);
  const keyToRank = new Map<string, number>();
  byDistance.forEach((c, i) => keyToRank.set(`${c.x},${c.y}`, i));
  const ringIndices: number[] = [];
  for (let i = 0; i < byDistance.length; i++) {
    ringIndices.push(getRingIndex(i));
  }
  const ringSizes: number[] = [];
  for (const r of ringIndices) {
    ringSizes[r] = (ringSizes[r] || 0) + 1;
  }
  const ordered = coordsInCanonicalOrder(coords);
  const elements = ordered.map(({ x, y }) => (
    <Hexagon
      key={x + "#" + y}
      x={x}
      y={y}
      loadRingIndex={ringIndices[keyToRank.get(`${x},${y}`) ?? 0]}
      loadGridId={loadGridId}
    />
  ));
  return { elements, ringSizes };
}
